import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, downloadFile } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Printer, FileSpreadsheet } from "lucide-react";

export default function PromotionReport() {
  const { year } = useParams();
  const nav = useNavigate();
  const { t, fmtMoney, fmtDate } = useI18n();
  const [rows, setRows] = useState(null);
  const allYears = year === "alle";

  useEffect(() => { api.get(`/promotions${allYears ? "" : `?year=${year}`}`).then((r) => setRows(r.data)).catch(() => setRows([])); }, [year, allYears]);
  if (rows === null) return <div className="text-muted-foreground p-8">{t("common.loading")}</div>;

  const grand = rows.reduce((s, r) => s + (r.total_cost || 0), 0);
  const grandQty = rows.reduce((s, r) => s + (r.quantity || 0), 0);
  const label = allYears ? t("common.allYears") : year;

  return (
    <div>
      <div className="flex items-center justify-between mb-4 print:hidden">
        <button onClick={() => nav(-1)} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="promo-report-back"><ArrowLeft className="h-4 w-4" /> {t("common.back")}</button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => downloadFile(`/promotions/export.xlsx${allYears ? "" : `?year=${year}`}`, `promotion_${year}.xlsx`)} data-testid="promo-report-excel"><FileSpreadsheet className="h-4 w-4 mr-1.5" /> Excel</Button>
          <Button onClick={() => window.print()} data-testid="promo-report-print"><Printer className="h-4 w-4 mr-1.5" /> {t("common.printPdf")}</Button>
        </div>
      </div>

      <div className="a4-page mx-auto max-w-3xl rounded-md border border-border bg-white text-black p-10 shadow-sm" data-testid="promo-report-doc" style={{ fontFamily: "'Lucida Sans', 'Lucida Grande', sans-serif" }}>
        <div className="flex justify-between items-start">
          <div>
            <div className="text-2xl font-bold">{t("pr.reportTitle", { label })}</div>
            <div className="text-xs text-neutral-600">Gluty-Free Club · Consberry GmbH · {t("ow.valuationNote")}</div>
          </div>
          <div className="text-right text-xs text-neutral-600">
            <div>{t("common.created")}: {fmtDate(new Date().toISOString())}</div>
            <div>{t("ow.positions", { count: rows.length })}</div>
          </div>
        </div>

        <p className="mt-4 text-xs text-neutral-600">{t("pr.reportIntro")}</p>

        <table className="w-full text-sm mt-6">
          <thead><tr className="border-b border-neutral-300 text-left">
            <th className="py-1">{t("common.date")}</th><th>SKU</th><th>{t("common.item")}</th><th>{t("pr.eventPurpose")}</th>
            <th className="text-right">{t("common.quantity")}</th><th className="text-right">{t("ow.costPerUnit")}</th><th className="text-right">{t("ow.costSum")}</th>
          </tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-neutral-100">
                <td className="py-1 whitespace-nowrap">{fmtDate(r.occurred_at)}</td>
                <td className="font-mono">{r.sku}</td><td>{r.product_name}</td>
                <td className="text-xs">{r.event || "—"}</td>
                <td className="text-right font-mono">{r.quantity}</td>
                <td className="text-right font-mono whitespace-nowrap">{fmtMoney(r.unit_cost)}</td>
                <td className="text-right font-mono whitespace-nowrap">{fmtMoney(r.total_cost)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="mt-8 border-t-2 border-neutral-400 pt-3 flex justify-end gap-8 text-sm">
          <div className="text-right">
            <div className="text-neutral-600">{t("pr.qtyTotalLabel", { label })}</div>
            <div className="text-xl font-bold font-mono">{grandQty}</div>
          </div>
          <div className="text-right">
            <div className="text-neutral-600">{t("pr.costTotalLabel", { label })}</div>
            <div className="text-xl font-bold font-mono">{fmtMoney(grand)}</div>
          </div>
        </div>
      </div>
    </div>
  );
}
